import { memo, useState } from "react";
import type { EnvironmentId } from "@cmmd-forge/contracts";
import { type TimestampFormat } from "@cmmd-forge/contracts/settings";
import type { ActivePlanState } from "../session-logic";
import type { LatestProposedPlanState } from "../session-logic";
import {
  proposedPlanTitle,
  buildProposedPlanMarkdownFilename,
  normalizePlanMarkdownForExport,
  downloadPlanAsTextFile,
  stripDisplayedPlanMarkdown,
} from "../proposedPlan";
import { toastManager } from "./ui/toast";
import { useCopyToClipboard } from "~/hooks/useCopyToClipboard";
import {
  isWorkspaceFileWriteCancelled,
  useWorkspaceFileWrite,
} from "~/hooks/useWorkspaceFileWrite";
import { CompactWorkspaceFileTransferStatus } from "./WorkspaceFileTransferStatus";
import { EmbeddedPlanActions, PlanSidebarContent, PlanSidebarHeader } from "./PlanSidebarSections";

interface PlanSidebarProps {
  activePlan: ActivePlanState | null;
  activeProposedPlan: LatestProposedPlanState | null;
  label?: string;
  environmentId: EnvironmentId;
  markdownCwd: string | undefined;
  workspaceRoot: string | undefined;
  timestampFormat: TimestampFormat;
  onClose: () => void;
  /** When true, renders without outer shell (width/border/header) — fills its parent container. */
  embedded?: boolean;
}

const PlanSidebar = memo(function PlanSidebar({
  activePlan,
  activeProposedPlan,
  label = "Plan",
  environmentId,
  markdownCwd,
  workspaceRoot,
  timestampFormat,
  onClose,
  embedded = false,
}: PlanSidebarProps) {
  const [proposedPlanExpanded, setProposedPlanExpanded] = useState(false);
  const workspaceWrite = useWorkspaceFileWrite(environmentId);
  const { copyToClipboard, isCopied } = useCopyToClipboard();

  const planMarkdown = activeProposedPlan?.planMarkdown ?? null;
  const displayedPlanMarkdown = planMarkdown ? stripDisplayedPlanMarkdown(planMarkdown) : null;
  const planTitle = planMarkdown ? proposedPlanTitle(planMarkdown) : null;
  const isSavingToWorkspace = workspaceWrite.state.status === "saving";
  const handleCopyPlan = () => {
    if (!planMarkdown) return;
    copyToClipboard(planMarkdown);
  };

  const handleDownload = () => {
    if (!planMarkdown) return;
    const filename = buildProposedPlanMarkdownFilename(planMarkdown);
    downloadPlanAsTextFile(filename, normalizePlanMarkdownForExport(planMarkdown));
  };

  const handleSaveToWorkspace = async () => {
    if (!workspaceRoot || !planMarkdown) return;
    const filename = buildProposedPlanMarkdownFilename(planMarkdown);
    try {
      const result = await workspaceWrite.save({
        cwd: workspaceRoot,
        relativePath: filename,
        contents: normalizePlanMarkdownForExport(planMarkdown),
      });
      toastManager.add({
        type: "success",
        title: "Plan saved",
        description: result.relativePath,
      });
    } catch (error) {
      toastManager.add(
        isWorkspaceFileWriteCancelled(error)
          ? {
              type: "info",
              title: "Save cancelled",
              description: "The incomplete transfer was discarded.",
            }
          : {
              type: "error",
              title: "Could not save plan",
              description: error instanceof Error ? error.message : "An error occurred.",
            },
      );
    }
  };

  const actionProps = {
    isCopied,
    isSaving: isSavingToWorkspace,
    canSave: Boolean(workspaceRoot),
    onCopy: handleCopyPlan,
    onDownload: handleDownload,
    onSave: () => void handleSaveToWorkspace(),
    onCancelSave: workspaceWrite.cancel,
  };

  return (
    <div
      className={
        embedded
          ? "flex h-full min-w-0 flex-col"
          : "flex h-full w-[340px] shrink-0 flex-col border-l border-border/70 bg-card/50"
      }
    >
      {!embedded ? (
        <PlanSidebarHeader
          label={label}
          activePlan={activePlan}
          hasPlanMarkdown={Boolean(planMarkdown)}
          timestampFormat={timestampFormat}
          onClose={onClose}
          {...actionProps}
        />
      ) : null}
      {embedded && planMarkdown ? (
        <EmbeddedPlanActions
          activePlan={activePlan}
          timestampFormat={timestampFormat}
          {...actionProps}
        />
      ) : null}
      <CompactWorkspaceFileTransferStatus
        state={workspaceWrite.state}
        onCancel={workspaceWrite.cancel}
      />
      <PlanSidebarContent
        activePlan={activePlan}
        activeProposedPlan={activeProposedPlan}
        displayedPlanMarkdown={displayedPlanMarkdown}
        planTitle={planTitle}
        markdownCwd={markdownCwd}
        proposedPlanExpanded={proposedPlanExpanded}
        onToggleProposedPlan={() => setProposedPlanExpanded((value) => !value)}
      />
    </div>
  );
});

export default PlanSidebar;
